import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ThemeMode } from '@ose/types';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly storageKey = 'themeMode';

  private readonly themeModeSubject: BehaviorSubject<ThemeMode>;

  readonly themeMode$: Observable<ThemeMode>;

  constructor(
    @Inject(DOCUMENT)
    private readonly document: Document
  ) {
    const initialTheme = this.getInitialTheme();

    this.themeModeSubject = new BehaviorSubject<ThemeMode>(initialTheme);

    this.themeMode$ = this.themeModeSubject.asObservable();

    this.applyTheme(initialTheme);
  }

  get currentTheme(): ThemeMode {
    return this.themeModeSubject.value;
  }

  getThemeMode(): Observable<ThemeMode> {
    return this.themeMode$;
  }

  toggleThemeMode(): void {
    const newTheme: ThemeMode = this.currentTheme === 'dark' ? 'light' : 'dark';

    this.setTheme(newTheme);
  }

  setTheme(theme: ThemeMode): void {
    this.applyTheme(theme);
    this.saveTheme(theme);
    this.themeModeSubject.next(theme);
  }

  private applyTheme(theme: ThemeMode): void {
    const htmlElement = this.document.documentElement;

    htmlElement.setAttribute('data-bs-theme', theme);
    htmlElement.style.colorScheme = theme;
  }

  private getInitialTheme(): ThemeMode {
    try {
      const savedTheme = this.document.defaultView?.localStorage.getItem(
        this.storageKey
      );

      if (savedTheme === 'light' || savedTheme === 'dark') {
        return savedTheme;
      }
    } catch (error) {
      console.error('Unable to read saved theme:', error);
    }

    return 'light';
  }

  private saveTheme(theme: ThemeMode): void {
    try {
      this.document.defaultView?.localStorage.setItem(this.storageKey, theme);
    } catch (error) {
      console.error('Unable to save theme:', error);
    }
  }
}
